/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import _ from 'lodash';
import dtm from 'scripts/services/dtm';
import Component from 'can/component/component';
import template from './live-multiline-player.stache';
import appContext from 'scripts/app-context';
import DomEventsManager from 'scripts/dom-events-manager';
import deviceUtil from 'scripts/utils/device';
import localStorage from 'scripts/services/localstorage-service';
import cloudinaryUtil from 'scripts/utils/cloudinary';
import {
    mapVideosForPlayer,
    YOUR_CLIPS_KEY,
} from 'components/video-landing/live-video-right-rail/live-video-right-rail-clips/live-video-right-rail-clips';

const brightcoveAccountId = window.globalSettings.brightcove.accountId;
const brightcovePlayerId =
    _.get(window.globalSettings, 'brightcove.live.playerId') || 'S1lRtXIhuM';
const youboraAccountCode = _.get(window.globalSettings, 'youbora', {})
    .accountCode;
const prerollUrl = _.get(window.globalSettings, 'brightcove.prerollUrl');

Component.extend({
    tag: 'live-multiline-player',
    template: template,
    leakScope: false,
    viewModel: {
        ended: null,
        define: {
            isPlayerReady: {
                type: 'boolean',
                value: false,
            },
            featuredEvent: {
                Type: can.Map,
                Value: can.Map,
            },
            activeChannels: {
                Type: Map.List,
                Value: '*',
            },
            activeView: {
                type: 'object',
                value: null,
            },
            channels: {
                Type: Map.List,
                Value: '*',
            },
            initialVideoId: {
                type: 'string',
                value: null,
            },
            transactionCode: {
                type: 'string',
                value: null,
            },
            player: {
                type: 'object',
            },
            highlightEnded: {
                type: 'number',
            },
            lastPlayerVisibility: {
                type: 'boolean',
                value: true,
            },
            updateChannelsTrigger: {
                type: 'number',
                value: 0,
            },
        },
        setVideoInSelected(video) {
            this.player.setVideoInSelected(video);
        },
    },
    events: {
        inserted() {
            DomEventsManager.subscribe(this);
            can.route(':initialVideoId', {});
            can.route.ready();
            this.viewModel.attr(
                'initialVideoId',
                can.route.attr('initialVideoId'),
            );
            this.viewModel.attr(
                'transactionCode',
                `Live multiline video ${new Date().toISOString()}`,
            );
            _.delay(this.onWindowScroll.bind(this), 300);

            let channels = this.viewModel.attr('channels');
            this.initMultiviewPlayer(channels);

            this.initSlateListeners();
        },

        initSlateListeners() {
            const slateCompute = can.compute(() => ({
                activeChannels: this.viewModel.attr('activeChannels'),
                cards: this.viewModel.attr('channels'),
            }));

            slateCompute.bind('change', (_, value) => {
                this.processSlates(value);
            });

            this.viewModel.bind('updateChannelsTrigger', () =>
                this.updateChannels(),
            );
        },

        processSlates(value) {
            const cards = value.cards.serialize();

            value.activeChannels.serialize().forEach((channelId, index) => {
                const currChannel = cards.find(
                    (card) => card.video.identifier === channelId,
                );

                if (!currChannel) {
                    return;
                }

                const addMethod =
                    index === 0
                        ? 'addSlateOverrideToMain'
                        : 'addSlateOverrideToMainPiP';
                const removeMethod =
                    index === 0
                        ? 'removeSlateOverrideToMain'
                        : 'removeSlateOverrideToMainPiP';

                if (!this.player) {
                    return;
                }

                if (currChannel.slate && currChannel.slate.isOn) {
                    const image = currChannel.slate.image;

                    const url = cloudinaryUtil.getCloudinaryPath(
                        image.identifier,
                        image.width,
                        image.height,
                        image.crop,
                        image.quality,
                        image.gravity,
                        image.radius,
                    );

                    this.player[addMethod](url);
                    this.player.getSelectedPlayer().exitPictureInPicture();
                } else {
                    // try catch not to throw error if no slate present
                    try {
                        this.player[removeMethod]();
                    } catch (e) {}
                }
            });
        },

        swapSlates() {
            this.processSlates({
                activeChannels: this.viewModel.attr('activeChannels'),
                cards: this.viewModel.attr('channels'),
            });
        },

        initMultiviewPlayer(channels) {
            if (channels && channels.length) {
                this.currentChannels = channels;
                let videos = channels.map((channel) => {
                    let video = {
                        id: channel.video.identifier,
                        header: channel.title,
                        title:
                            this.taggedPlayers(channel) +
                            ' ' +
                            this.taggedHoles(channel),
                        tags: null,
                        autoplay: true,
                    };

                    if (channel.playerTagGroups) {
                        video.tags = channel.playerTagGroups
                            .attr(0)
                            .map((tag) => {
                                return tag.title;
                            })
                            .join(', ');
                    }

                    if (channel.holeTags && !video.tags) {
                        video.tags = channel.holeTags
                            .map((tag) => {
                                return tag.title;
                            })
                            .join(', ');
                    }

                    return video;
                });
                this.createPlayer(videos.serialize());
            }
        },

        needToCreatePlayer(videos) {
            const activeChannels = this.viewModel.attr('activeChannels');

            if (activeChannels.length === 0) {
                return true;
            }
            const videoIds = videos.map((video) => video.id);
            return _.some(
                activeChannels,
                (channel) => !_.includes(videoIds, channel),
            );
        },

        createPlayer(videos) {
            this.updateChannels();

            if (!this.needToCreatePlayer(videos)) {
                return;
            }

            let adServerURL = prerollUrl;
            if (this.player) {
                adServerURL = '';
                this.player.destroyAllPlayers();
                this.viewModel.attr('activeChannels', videos);
            }

            const self = this;
            let iframe = document.querySelector('#live-multiline-player-iframe')
                .contentWindow;
            if (!iframe.USGAPlayer) {
                return setTimeout(() => {
                    this.createPlayer(videos);
                }, 500);
            }
            this.multiviewPlayerIframe = iframe;

            let hashVideoId = this.viewModel.attr('initialVideoId');
            if (hashVideoId) {
                const filtered = videos.filter((v) => v.id === hashVideoId);
                videos = filtered.length === 1 ? filtered : videos;
            }

            const layout = this.currentLayout || 'single';

            this.player = new iframe.USGAPlayer({
                account: brightcoveAccountId,
                player: brightcovePlayerId,
                youboraAccountCode: youboraAccountCode,
                xApiKey: 'GRyYNpviD96MTDbQfiYTn3VB0ZNpECb1EpvrZUS6',
                adServerURL: adServerURL,
                debug: false,
                layout: layout,
                videos,
            });

            this.viewModel.attr('player', this.player);

            window.addEventListener('message', function(e) {
                if (e.data === 'selectMainPlayer')
                    self.player.selectPlayer('main');
            });

            this.player.on('viewChange', (e) => {
                this.updateChannels();

                this.multiviewPlayerIframe
                    .$('#videos .video-js')
                    .each(function() {
                        const vjsPlayer = self.multiviewPlayerIframe.videojs(
                            this.id,
                        );

                        vjsPlayer.ready(function() {
                            const oneFullscreenChange = () => {
                                if (!this.isFullscreen() && this.paused()) {
                                    this.play();
                                }
                            };

                            this.on('pause', () => {
                                if (this.isFullscreen()) {
                                    this.one(
                                        'fullscreenchange',
                                        oneFullscreenChange,
                                    );
                                    setTimeout(() => {
                                        this.off(
                                            'fullscreenchange',
                                            oneFullscreenChange,
                                        );
                                    }, 500);
                                }
                            });

                            this.on('fullscreenchange', () => {
                                const isFullscreen = this.isFullscreen();
                                self.viewModel.dispatch('fullscreenchange', [
                                    isFullscreen,
                                ]);
                                //only for iOS devices: force playing after exit fullscreen mode by swipe down because iOS stops video
                                if (
                                    deviceUtil.isIos() &&
                                    !isFullscreen &&
                                    !this.paused()
                                ) {
                                    this.one('pause', () => {
                                        this.play();
                                    });
                                }
                            });
                        });
                    });
            });

            this.player.on('userSelectedView', (e) => {
                let dtmLiveView;
                this.currentLayout = e.data.view;
                switch (e.data.view) {
                    case 'mosaic': {
                        dtmLiveView = 'Mosaic Watch Live';
                        break;
                    }
                    case 'single': {
                        dtmLiveView = 'Single Watch Live';
                        break;
                    }
                    case 'pip': {
                        dtmLiveView = 'PIP Watch Live';
                        break;
                    }
                    case 'split': {
                        dtmLiveView = 'Spit Watch Live';
                        break;
                    }
                }
                dtm.trackLiveVideoViewChange(dtmLiveView);
            });

            this.player.on('userSelectedVideo', () => {
                this.updateChannels();
            });

            this.player.on('swapPip', () => {
                this.updateChannels();
                dtm.trackPipChannelSwap();
            });
            this.player.on('videoLoaded', () => {
                this.updateChannels();
            });
            this.player.on('error', (error) => {
                console.log(error);
            });
            this.player.on('highlightVideoStart', (it) => {
                const { currentVideoIndex, currentVideo } = it.data;

                if (currentVideoIndex === 0) {
                    dtm.trackYourClipsVideoLaunches(
                        currentVideo.id,
                        currentVideo.title,
                        currentVideo.roundNumber,
                        currentVideo.holeNumber,
                    );
                } else {
                    dtm.trackYourClipsVideoNextVideo(
                        currentVideo.id,
                        currentVideo.title,
                        currentVideo.roundNumber,
                        currentVideo.holeNumber,
                    );
                }
            });
            this.player.on('highlightVideoEnd', (it) => {
                const videos = localStorage.getItem(YOUR_CLIPS_KEY) || [];
                const id = it.data.currentVideo.id;

                if (videos.includes(id)) {
                    return;
                }

                localStorage.setItem(
                    YOUR_CLIPS_KEY,
                    videos.concat(it.data.currentVideo.id),
                );

                this.viewModel.attr('highlightEnded', Math.random());
            });
            this.player.on('highlightPlaylistEnd', () => {
                if (_.get($('right-rail-clip').viewModel(), 'data.videos')) {
                    // dirty hack for player to set new playlist correctly
                    setTimeout(() => {
                        this.player.setHighlightInPiP(
                            {
                                videos: mapVideosForPlayer(
                                    $('right-rail-clip').viewModel(),
                                ),
                            },
                            0,
                        );
                    }, 0);
                } else {
                    this.player.toggleView();
                }
            });

            this.viewModel.attr('isPlayerReady', true);
            this.viewModel.attr('player', this.player);
        },

        taggedPlayers(channel) {
            let playerTagGroups = channel.playerTagGroups;
            if (playerTagGroups) {
                let taggedPlayers = [];
                playerTagGroups.each((ptg) => {
                    ptg.each((pl) => {
                        taggedPlayers.push(this.shortenName(pl.title));
                    });
                });
                return [...new Set(taggedPlayers)].join(', ');
            }
            return '';
        },

        taggedHoles(channel) {
            let holeTags = channel.holeTags;
            return holeTags
                ? 'Holes: ' +
                      holeTags
                          .map((ht) => {
                              return ht.title;
                          })
                          .join(', ')
                : '';
        },

        shortenName(name) {
            let nameArr = name.split(' ');
            if (nameArr.length > 1) {
                nameArr[0] = `${nameArr[0].charAt(0)}.`;
                name = nameArr.join(' ');
            }
            return name;
        },

        isMultiviewAllowed() {
            return (
                ['extra-large', 'large'].indexOf(
                    appContext.model.screen.attr('screenSize'),
                ) !== -1
            );
        },

        onLayout: function(layout) {
            if (this.layout && this.layout !== layout) {
                this.updateChannels();
            }
            this.layout = layout;
        },

        removed() {
            DomEventsManager.unsubscribe(this);
        },

        getVideoDispositionMap() {
            if (!this.player) {
                return {
                    view: 'single',
                    videos: [],
                };
            }

            const view = this.player.getSelectedView();

            const videos = [this.player.getVideoIdLoadedInMainPlayer()];

            if (this.player && this.player.getSelectedView() === 'pip') {
                videos.push(this.player.getVideoIdLoadedInPipPlayer());
            }

            return { view, videos };
        },

        updateChannels() {
            const activeView = this.getVideoDispositionMap();
            this.viewModel.attr('activeView', activeView);
            this.viewModel.attr('activeChannels', activeView.videos);
        },

        '{scope} channels'(scope, event, channels) {
            if (!_.isEqual(channels, this.currentChannels)) {
                this.currentChannels = channels;
                this.initMultiviewPlayer(channels);
            }
        },

        checkVisible(elm) {
            const windowHeight = $(window).height(),
                topOffset = $(window).scrollTop(),
                elementOffset = $(elm).offset().top,
                elementHeight = $(elm).height();

            return (
                elementOffset < windowHeight + topOffset &&
                elementOffset > topOffset - elementHeight
            );
        },

        onWindowScroll: function() {
            const visible = this.checkVisible('#live-multiline-player-iframe');
            const lastPlayerVisibility = this.viewModel.attr(
                'lastPlayerVisibility',
            );

            if (
                lastPlayerVisibility !== undefined &&
                visible === lastPlayerVisibility
            ) {
                return;
            }

            const activeChannel = _.find(
                this.viewModel.attr('channels'),
                (it) =>
                    this.player &&
                    it.video.identifier ===
                        this.player.getVideoIdLoadedInMainPlayer(),
            );
            const hasSlate =
                activeChannel &&
                activeChannel.slate &&
                activeChannel.slate.isOn;

            if (this.player && !visible && !hasSlate) {
                this.player.getSelectedPlayer().requestPictureInPicture();
            }

            this.viewModel.attr('lastPlayerVisibility', visible);
        },

        '{document} .s-video-right-rail-row click'(element, event) {
            let rowViewModel = element.parent().viewModel();

            if (
                this.viewModel
                    .attr('activeChannels')
                    .indexOf(rowViewModel.attr('data.video.identifier')) !== -1
            ) {
                return;
            }

            this.player.setVideoInSelected({
                id: rowViewModel.attr('data.video.identifier'),
                header: rowViewModel.attr('data.title'),
                title:
                    this.taggedPlayers(rowViewModel.attr('data')) +
                    ' ' +
                    this.taggedHoles(rowViewModel.attr('data')),
                autoplay: true,
            });
            setTimeout(() => {
                this.updateChannels();
            }, 100);
        },
    },
});
